import { isNotEmpty, isArray, refactorPermissions, floorKeep, beautifyThePrice, beautifyTheLowHighObject } from '@lhb/func';
import { ReportData, ReportMedia } from './types';

// 需求边界（2026-1029迭代/商场慢闪场景体验优化，文档：https://confluence.lanhanba.com/pages/viewpage.action?pageId=172108153）：实付租金及费用、租售比、净利、净利率、回本周期均由后端计算修复；前端只映射/格式化服务端结果，不补算或在缺字段时自行兜底推导。

export const surveyFields = [
  { identification: 'builtArea', label: '面积', unit: '㎡' },
  { identification: 'groundFloor', label: '所在楼层', unit: '' },
  { identification: 'basicMallStoreyHeight', label: '层高', unit: 'm' },
  { identification: 'unloadingZone', label: '点位备注', unit: '' },
];

export function emptyReport(): ReportData {
  return {
    permissions: [],
    name: '', address: '', city: '', mallName: '', mallMatched: false,
    survey: surveyFields.map(field => ({ label: field.label, value: null })),
    media: { layout: [], scene: [], videos: [] },
    mall: { metrics: [], daily: [], dailyAverage: null, monthly: [] },
    neighbors: [],
    traffic: { metrics: [], weekdays: [], gender: [], hourly: [], ages: [], formula: '', mallShare: '' },
  };
}

export function parseFieldValue(value: unknown): any {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return value; }
}

function readBusinessDistrictType(value: unknown): string | undefined {
  const parsed = parseFieldValue(value);
  if (!parsed || typeof parsed !== 'object') return undefined;
  const type = parsed.clusterTypeName || parsed.manualTypeName;
  return typeof type === 'string' && type.trim() ? type.trim() : undefined;
}

function getBusinessDistrictType(detail: any, fields: Record<string, any>): string | undefined {
  const directType = [detail?.businessDistrictType, detail?.clusterTypeName, detail?.manualTypeName]
    .find(value => typeof value === 'string' && value.trim());
  if (directType) return directType.trim();

  return Object.values(fields)
    .filter((field: any) => [28, 30].includes(Number(field?.controlType)))
    .map((field: any) => readBusinessDistrictType(field?.textValue))
    .find(Boolean);
}

export function reportMedia(value: unknown): ReportMedia[] {
  const parsed = parseFieldValue(value);
  if (!Array.isArray(parsed)) return [];
  return parsed.reduce<ReportMedia[]>((result, item) => {
    const url = typeof item === 'string' ? item : item?.url;
    if (typeof url === 'string' && /^https?:\/\//i.test(url)) {
      result.push({ url, name: typeof item?.name === 'string' ? item.name : '点位媒体', thumbnail: item?.thumbnail });
    }
    return result;
  }, []);
}

// Traverse only documented form containers; hidden ancestors suppress their descendants.
export function collectReportFields(detail: any): Record<string, any> {
  const fields: Record<string, any[]> = {};
  const hidden = new Set<string>();
  const visit = (items: any[], suppressed = false) => {
    if (!isArray(items)) return;
    items.forEach(item => {
      if (!item || typeof item !== 'object') return;
      const denied = suppressed || item.access === 3 || item.isShow === 0;
      if (item.identification) {
        if (denied) hidden.add(item.identification);
        else (fields[item.identification] ||= []).push(item);
      }
      ['childList', 'propertyConfigVOList', 'formConfigList'].forEach(key => visit(item[key], denied));
    });
  };
  (detail?.moduleDetails || []).forEach((module: any) => {
    visit(module.importModule, module.isShow === 0);
    visit(module.infoModule?.propertyGroupVOList, module.isShow === 0);
  });
  const result: Record<string, any> = {};
  Object.keys(fields).forEach(key => {
    const candidates = fields[key];
    // Ambiguous repeated values are omitted until the backend selection contract is known.
    if (!hidden.has(key) && candidates.every(field => field.textValue === candidates[0].textValue)) result[key] = candidates[0];
  });
  return result;
}

export function adaptReportDetail(detail: any): ReportData {
  const result = emptyReport();
  const fields = collectReportFields(detail);
  result.name = detail?.name || '';
  result.businessDistrictType = getBusinessDistrictType(detail, fields);
  result.address = detail?.address || '';
  result.city = [detail?.cityName, detail?.districtName].filter(Boolean).join(' ');
  result.mallName = detail?.placeName || '';
  result.mallMatched = isNotEmpty(detail?.placeId);
  result.survey = surveyFields.map(meta => {
    const field = fields[meta.identification];
    let value = parseFieldValue(field?.textValue);
    if (meta.identification === 'groundFloor' && isNotEmpty(value)) {
      const selected = Array.isArray(value) ? value[0] : value;
      const option = field?.propertyConfigOptionVOList?.find((item: any) => String(item.id) === String(selected));
      value = option?.name || (typeof selected === 'string' && !/^\d+$/.test(selected) ? selected : null);
    }
    return { label: meta.label, value: isNotEmpty(value) && ['number', 'string'].includes(typeof value) ? `${value}${meta.unit}` : null };
  });
  result.media = {
    layout: reportMedia(fields.mallLayoutDiagram?.textValue),
    scene: reportMedia(fields.surroundingScenery?.textValue),
    videos: reportMedia(fields.relatedVideos?.textValue),
  };
  return result;
}

const unwrap = (value: any) => value?.data?.data || value?.data || value || {};

export function adaptMallReportApis(detailResponse: any, mallResponse: any, pointResponse: any, flowResponse: any, businessResponse?: any, smartResponse?: any): ReportData {
  const result = adaptReportDetail(unwrap(detailResponse));
  const detail = unwrap(detailResponse);
  result.overview = {
    projectLevel: unwrap(mallResponse).overview?.projectLevel,
    marketScoreRank: unwrap(mallResponse).overview?.marketScoreRank,
    dailyFlow: unwrap(flowResponse).dailyFlow,
    peakRange: unwrap(flowResponse).peakRange,
    monthlyRent: unwrap(businessResponse).monthlyRent,
    monthExpense: unwrap(detailResponse).businessInfo?.monthExpense,
    businessType: unwrap(businessResponse).businessType,
    deductionRate: unwrap(businessResponse).deductionRate,
    leasePeriod: unwrap(businessResponse).leasePeriod,
    paybackPeriod: unwrap(smartResponse).summary?.paybackPeriod,
    priceToRentRatio: unwrap(smartResponse).summary?.priceToRentRatio,
    estimatedMonthlyRevenue: unwrap(smartResponse).summary?.monthlySales ?? detail.smartDecision?.estimatedMonthlyRevenue,
  };
  // 联调范围（2026-1029迭代/商场慢闪场景体验优化，文档：https://confluence.lanhanba.com/pages/viewpage.action?pageId=172108153）：客流授权查询/同步/角色映射由后端排查修复；前端按既有契约展示并回归有权限的拓店/审批角色，不在客户端放宽权限或新增权限模型。
  result.permissions = refactorPermissions((Array.isArray(detail.permissions) ? detail.permissions : []).filter(item => item && typeof item.event === 'string' && typeof item.name === 'string'));
  const mall = unwrap(mallResponse);
  const point = unwrap(pointResponse);
  const flow = unwrap(flowResponse);
  const overview = mall.overview || detail.mallSituation || {};
  result.name = detail.name || result.name;
  result.businessDistrictType = getBusinessDistrictType(detail, collectReportFields(detail)) || result.businessDistrictType;
  result.address = detail.address || result.address;
  result.city = [detail.cityName, detail.districtName].filter(Boolean).join(' ') || result.city;
  result.mallName = detail.placeName || detail.mallName || result.mallName;
  result.mallMatched = isNotEmpty(mall.modelClusterId) || result.mallMatched;
  const lng = Number(detail.lng);
  const lat = Number(detail.lat);
  if (isNotEmpty(detail.lng) && isNotEmpty(detail.lat) && Number.isFinite(lng) && Number.isFinite(lat) && Math.abs(lng) <= 180 && Math.abs(lat) <= 90) {
    result.mall.indoor = { center: [lng, lat] };
    result.mallMatched = true;
  }
  const area = point.area ?? detail.area;
  const height = point.heightLimit ?? detail.spotInfo?.heightLimit;
  result.survey = [
    { label: '面积', value: isNotEmpty(area) ? `${area}㎡` : null },
    { label: '所在楼层', value: point.floor ?? null },
    { label: '层高', value: isNotEmpty(height) ? `${height}m` : null },
    { label: '点位备注', value: point.remark ?? null },
  ];
  const gallery = detail.gallery || {};
  result.media = {
    layout: reportMedia(gallery.locationPics),
    scene: reportMedia(gallery.surroundingPics),
    videos: reportMedia(gallery.movementPics),
  };
  result.mall.metrics = [
    { label: '商场等级', value: [overview.projectLevel, isNotEmpty(overview.marketScore) ? `评分 ${overview.marketScore}/100` : null].filter(isNotEmpty).join(' · ') || null },
    { label: '城市客流排名', value: overview.flowRankText ?? null },
    { label: '工作日客流', value: isNotEmpty(overview.workdayDailyFlow) ? `${overview.workdayDailyFlow}人/日` : null },
    { label: '节假日客流', value: isNotEmpty(overview.holidayDailyFlow) ? `${overview.holidayDailyFlow}人/日` : null },
  ];
  const dailyTrend = mall.dailyFlowTrend || {};
  result.mall.daily = (dailyTrend.dateList || []).map((label: string, index: number) => ({ label, value: dailyTrend.dailyFlowList?.[index] ?? null }));
  result.mall.dailyAverage = dailyTrend.monthlyAvgFlow ?? null;
  const monthly = mall.monthlyAvgFlowTrend || {};
  result.mall.monthly = (monthly.month || []).map((label: string, index: number) => ({
    label, baseline: monthly.thisYear?.[index] ?? null, comparison: monthly.lastYear?.[index] ?? null, share: monthly.flowRate?.[index] ?? null,
  }));
  // 接口未提供品类与方位编码枚举；优先展示方位名称，不根据影响系数推断关系。
  result.neighbors = (Array.isArray(point.nearbyBrands) ? point.nearbyBrands : []).filter(Boolean).map((item: any) => ({
    position: item.positionName?.trim() || item.position || '方位未提供',
    name: item.brandName || '-',
    category: '',
    effect: item.relationName || '',
    logo: item.brandLogo || undefined,
  }));
  result.traffic.metrics = [
    { label: '日均过店客流', value: isNotEmpty(flow.dailyFlow) ? `${flow.dailyFlow}人/日` : null },
    { label: '峰值时段', value: flow.peakRange ?? null },
    { label: '主要客群', value: flow.mainCustomerGroup ?? null },
  ];
  result.traffic.weekdays = [['工作日', flow.workdayAvgFlow], ['周末', flow.weekendAvgFlow]].map(([label, value]) => ({ label, value: value == null ? null : Number(value) }));
  result.traffic.formula = flow.dailyFlowFormula ?? '';
  result.traffic.hourly = (flow.hourlyFlows || []).map((item: any) => ({ label: `${item.hour}时`, value: item.flow ?? null }));
  result.traffic.gender = (flow.genderDistribution || []).map((item: any) => ({ label: item.name, value: item.percentage ?? null }));
  result.traffic.ages = (flow.ageDistribution || []).map((item: any) => ({ label: item.name, value: item.percentage ?? null }));
  return result;
}

export function reportNavigationItems(data: ReportData) {
  const mallLevel = data.overview ? data.overview.projectLevel : data.mall.metrics.find(item => item.label === '商场等级')?.value;
  const mallRank = isNotEmpty(data.overview?.marketScoreRank) ? `城市排名第${data.overview?.marketScoreRank}名` : null;
  const point = data.survey.slice(0, 2).map(item => item.value).filter(isNotEmpty).join(' · ');
  const businessType = String(data.overview?.businessType || '');
  const monthlyRent = data.overview?.monthlyRent;
  const rent = isNotEmpty(monthlyRent) ? `${beautifyThePrice(floorKeep(Number(monthlyRent), 10000, 4, 1), ',', 'auto')}万/月` : '';
  const deduction = isNotEmpty(data.overview?.deductionRate) ? `扣点${data.overview?.deductionRate}%` : '';
  const lease = isNotEmpty(data.overview?.leasePeriod) ? `${data.overview?.leasePeriod}月租期` : '';
  const businessBase = businessType === '1' ? rent : businessType === '2' ? deduction : businessType === '3' ? [rent, deduction].filter(isNotEmpty).join('+') : businessType === '4' ? [rent, deduction].filter(isNotEmpty).join('/') + '取高' : '';
  const business = businessBase;
  const estimatedMonthlyRevenue = data.overview?.estimatedMonthlyRevenue;
  const decisionRevenue = typeof estimatedMonthlyRevenue === 'number' && Number.isFinite(estimatedMonthlyRevenue)
    ? beautifyTheLowHighObject(1, estimatedMonthlyRevenue, 2, { low: '元', high: '万元', top: '亿元' })
    : null;
  const decision = decisionRevenue ? `预估 ${decisionRevenue.val}${decisionRevenue.unit}/月` : '暂无数据';
  const decisionHint = [
    isNotEmpty(data.overview?.paybackPeriod) ? `回本 ${data.overview?.paybackPeriod}月` : null,
    isNotEmpty(data.overview?.priceToRentRatio) ? `租售比 ${data.overview?.priceToRentRatio}%` : null,
  ].filter(isNotEmpty).join(' · ') || '销售额预估';
  return [
    ...(data.mallMatched ? [{ key: 'mall', label: '商场情况', summary: [mallLevel, mallRank].filter(isNotEmpty).join(' · ') || '暂无数据' }] : []),
    { key: 'spot', label: '点位信息', summary: point || '-' },
    { key: 'traffic', label: '客流情况', summary: isNotEmpty(data.overview?.dailyFlow) ? `${data.overview?.dailyFlow}人/日` : '暂无数据', hint: data.overview?.peakRange ? `日均客流 · ${String(data.overview.peakRange).replace(/:00/g, '').replace(/-/g, '–')}峰值` : '日均客流 · 峰值时段' },
    { key: 'business', label: '商务条件', summary: business || '暂无数据', hint: lease ? `月租金 · ${lease}` : '月租金 · 租期' },
    { key: 'decision', label: '智能决策', summary: decision, hint: decisionHint, tag: 'AI预测' },
  ];
}

export function fixtureScenario(search: string, development: boolean): string | null {
  if (!development || !new URLSearchParams(search).has('mallReportFixture')) return null;
  // 报告已切换真实接口，保留调用签名以兼容既有开发入口。
  return null;
}
